import { useEffect } from 'react'
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom'

import NonAuthLayout from '../Layouts/NonAuthLayout'
import NewLayout from '../Layouts/NewLayout'

import { authProtectedRoutes, publicRoutes } from './allRoutes'
import AuthProtected from './AuthProtected'
import LazyPage from './LazyPage'
import { lazyWithChunkRetry } from './lazyWithChunkRetry'
import { ErrorBoundary } from 'Components/Common/ErrorBoundary'
import { ChunkLoadErrorBoundary } from 'Components/Common/ChunkLoadErrorBoundary'
import { ViewModeGuard } from 'Components/Security/ViewModeGuard'
import { FeatureGuard } from 'Components/Security/FeatureGuard'
import { PermissionGuard } from 'Components/Security/PermissionGuard'
import { AppToaster } from 'design-system/components/app-toaster'

const CategoriesRoutes = lazyWithChunkRetry(() => import('./categories'))
const SuppliersRoutes = lazyWithChunkRetry(() => import('./suppliers'))
const ProductsRoutes = lazyWithChunkRetry(() => import('./products'))
const RawMaterialsPage = lazyWithChunkRetry(() => import('../pages/RawMaterials'))
const PurchasesRoutes = lazyWithChunkRetry(() => import('./purchases'))
const CustomersRoutes = lazyWithChunkRetry(() => import('./customers'))
const MarketingRoutes = lazyWithChunkRetry(() => import('./marketing'))
const POSRoutes = lazyWithChunkRetry(() => import('./pos'))
const ReportsRoutes = lazyWithChunkRetry(() => import('./reports'))
const SettingsRoutes = lazyWithChunkRetry(() => import('./settings'))
const InventoryRoutes = lazyWithChunkRetry(() => import('./inventory'))
const BackupRoutes = lazyWithChunkRetry(() => import('./backup'))
const BarcodePrintRoutes = lazyWithChunkRetry(() => import('./barcodePrint'))
const SalesRoutes = lazyWithChunkRetry(() => import('./sales'))
const ActivityLog = lazyWithChunkRetry(() => import('../pages/ActivityLog'))
const NotificationsPage = lazyWithChunkRetry(() => import('../pages/Notifications'))
const EmployeesHub = lazyWithChunkRetry(() => import('../pages/Employees'))
const PaymentsPage = lazyWithChunkRetry(() => import('../pages/Payments'))
const ExpensesList = lazyWithChunkRetry(() => import('../pages/Expenses/list'))
const BudgetsPage = lazyWithChunkRetry(() => import('../pages/Budgets'))
const DeliveryList = lazyWithChunkRetry(() => import('../pages/Delivery/list'))
const DeliveryFinanceDashboardPage = lazyWithChunkRetry(() => import('../pages/DeliveryFinance/Dashboard'))
const DeliveryFinanceLiabilitiesPage = lazyWithChunkRetry(() => import('../pages/DeliveryFinance/Liabilities'))
const DeliveryFinanceSettlementsPage = lazyWithChunkRetry(() => import('../pages/DeliveryFinance/Settlements'))
const DeliveryFinanceAlertsPage = lazyWithChunkRetry(() => import('../pages/DeliveryFinance/Alerts'))
const DeliveryFinanceDriverDetailPage = lazyWithChunkRetry(() => import('../pages/DeliveryFinance/DriverDetail'))
const DriverSettlementsPage = lazyWithChunkRetry(() => import('../pages/DriverSettlements'))
const DriversPage = lazyWithChunkRetry(() => import('../pages/Drivers'))
const DeliveryZonesPage = lazyWithChunkRetry(() => import('../pages/DeliveryZones'))
const GiftCardsList = lazyWithChunkRetry(() => import('../pages/GiftCards/list'))
const BranchesList = lazyWithChunkRetry(() => import('../pages/Branches'))
const BranchDetail = lazyWithChunkRetry(() => import('../pages/Branches/BranchDetail'))
const DiningHallsPage = lazyWithChunkRetry(() => import('../pages/DiningHalls'))
const FinancialAccountsPage = lazyWithChunkRetry(() => import('../pages/FinancialAccounts'))
const VaultTransactionsPage = lazyWithChunkRetry(() => import('../pages/Vaults/VaultTransactions'))
const VaultTransactionDetailsPage = lazyWithChunkRetry(() => import('../pages/Vaults/VaultTransactionDetails'))
const ShiftsManagement = lazyWithChunkRetry(() => import('../pages/Shifts/ShiftsManagement'))
const PartnerReportsPage = lazyWithChunkRetry(() => import('../pages/Reports/PartnerReports'))
const WaiterPOSPage = lazyWithChunkRetry(() => import('../pages/Waiter/WaiterPOS'))
const KitchenDisplayPage = lazyWithChunkRetry(() => import('../pages/Kitchen/KitchenDisplay'))
const KitchenTicketPrintPage = lazyWithChunkRetry(() => import('../pages/Kitchen/KitchenTicketPrint'))
const KitchenPrintJobsPage = lazyWithChunkRetry(() => import('../pages/Kitchen/KitchenPrintJobs'))
const KitchenStationsPage = lazyWithChunkRetry(() => import('../pages/Kitchen/KitchenStations'))
const SupplierPaymentsPage = lazyWithChunkRetry(() => import('../pages/Purchases/Payments/SupplierPaymentsList'))

const Index = () => {
 const navigate = useNavigate()

 useEffect(() => {
 const handleUnauthorized = () => {
 navigate('/login', { replace: true })
 }
 window.addEventListener('auth:unauthorized', handleUnauthorized)
 return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
 }, [navigate])

 return (
 <ErrorBoundary>
 <AppToaster />
 <Routes>
 {publicRoutes.map((route, idx) => (
 <Route
 path={route.path}
 element={<NonAuthLayout><LazyPage>{route.component}</LazyPage></NonAuthLayout>}
 key={idx}
 />
 ))}
 <Route
 path="/categories"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_categories">
 <LazyPage><CategoriesRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/suppliers/*"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_suppliers">
 <LazyPage><SuppliersRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/products/*"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_products">
 <LazyPage><ProductsRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
	 />
	 <Route
	 path="/raw-materials"element={
	 <AuthProtected>
	 <NewLayout>
	 <FeatureGuard feature="inventory"><PermissionGuard permission="manage_products">
	 <LazyPage><RawMaterialsPage /></LazyPage>
	 </PermissionGuard></FeatureGuard>
	 </NewLayout>
	 </AuthProtected>
	 }
	 />
	 <Route
	 path="/purchases/*"element={
 <AuthProtected>
 <NewLayout>
 <FeatureGuard feature="purchases"><PermissionGuard permission="manage_purchases">
 <LazyPage><PurchasesRoutes /></LazyPage>
 </PermissionGuard></FeatureGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/supplier-payments"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_supplier_payments">
 <LazyPage><SupplierPaymentsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/customers/*"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['manage_customers', 'process_sales']}>
 <LazyPage><CustomersRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/marketing/*"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['manage_promotions', 'manage_coupons']}>
 <LazyPage><MarketingRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/pos"element={
 <AuthProtected>
 <NewLayout>
 <ChunkLoadErrorBoundary><ViewModeGuard allowedModes={['branch']}><FeatureGuard feature="pos"><PermissionGuard permission="process_sales"><LazyPage><POSRoutes /></LazyPage></PermissionGuard></FeatureGuard></ViewModeGuard></ChunkLoadErrorBoundary>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/reports/partner"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="view_partner_reports">
 <LazyPage><PartnerReportsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/reports/*"element={
 <AuthProtected>
 <NewLayout>
 <ErrorBoundary><FeatureGuard feature="reports"><PermissionGuard permission="view_reports"><LazyPage><ReportsRoutes /></LazyPage></PermissionGuard></FeatureGuard></ErrorBoundary>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/settings"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_settings">
 <LazyPage><SettingsRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 {/* Standalone hardware settings removed — device/printer settings live in Branch Settings (/branches). */}
 <Route
 path="/hardware-settings"element={
 <AuthProtected>
 <Navigate to="/branches"replace />
 </AuthProtected>
 }
 />
 <Route
 path="/inventory/*"element={
 <AuthProtected>
 <NewLayout>
 <FeatureGuard feature="inventory"><PermissionGuard permission="manage_inventory">
 <LazyPage><InventoryRoutes /></LazyPage>
 </PermissionGuard></FeatureGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/backup"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['global']}><PermissionGuard permission="manage_settings"><LazyPage><BackupRoutes /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/barcode-print"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission="manage_products"><LazyPage><BarcodePrintRoutes /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/sales/*"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="process_sales">
 <LazyPage><SalesRoutes /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/activity-logs"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="view_activity_logs">
 <LazyPage><ActivityLog /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/notifications"
 element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="view_dashboard">
 <LazyPage>
 <NotificationsPage />
 </LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/employees"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['manage_users', 'manage_employees', 'view_payroll', 'manage_attendance', 'manage_payroll_adjustments']}>
 <LazyPage><EmployeesHub /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/payments"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_treasuries">
 <LazyPage><PaymentsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/expenses"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['process_sales', 'manage_expenses']}>
 <LazyPage><ExpensesList /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/budgets"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['view_budgets', 'manage_budgets']}>
 <LazyPage><BudgetsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['process_sales', 'manage_deliveries']}>
 <LazyPage><DeliveryList /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/drivers"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_drivers">
 <LazyPage><DriversPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery-zones"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_deliveries">
 <LazyPage><DeliveryZonesPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/driver-settlements"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['delivery_view_settlements', 'delivery_settle', 'manage_deliveries']}>
 <LazyPage><DriverSettlementsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery-finance"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['delivery_view_settlements', 'delivery_settle', 'manage_deliveries']}>
 <LazyPage><DeliveryFinanceDashboardPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery-finance/liabilities"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['delivery_view_settlements', 'delivery_settle', 'manage_deliveries']}>
 <LazyPage><DeliveryFinanceLiabilitiesPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery-finance/settlements"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['delivery_view_settlements', 'delivery_settle', 'manage_deliveries']}>
 <LazyPage><DeliveryFinanceSettlementsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery-finance/alerts"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['delivery_view_settlements', 'delivery_settle', 'manage_deliveries']}>
 <LazyPage><DeliveryFinanceAlertsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/delivery-finance/drivers/:driverId"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['delivery_view_settlements', 'delivery_settle', 'manage_deliveries']}>
 <LazyPage><DeliveryFinanceDriverDetailPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/gift-cards"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="manage_gift_cards">
 <LazyPage><GiftCardsList /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/branches"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['global']}><FeatureGuard feature="branches"><PermissionGuard permission="manage_branches"><LazyPage><BranchesList /></LazyPage></PermissionGuard></FeatureGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/branches/:id"element={
 <AuthProtected>
 <NewLayout>
 <FeatureGuard feature="branches"><PermissionGuard permission="manage_branches">
 <LazyPage><BranchDetail /></LazyPage>
 </PermissionGuard></FeatureGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/financial-accounts"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission={['view_account_balances', 'manage_financial_accounts', 'manage_treasuries', 'access_admin_routes']}>
 <LazyPage><FinancialAccountsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/vaults"element={
 <Navigate to="/financial-accounts?tab=cash" replace />
 }
 />
 <Route
 path="/vaults/archive"element={
 <Navigate to="/financial-accounts" replace />
 }
 />
 <Route
 path="/vaults/transactions"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="access_admin_routes">
 <LazyPage><VaultTransactionsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/vaults/transactions/:id"element={
 <AuthProtected>
 <NewLayout>
 <PermissionGuard permission="access_admin_routes">
 <LazyPage><VaultTransactionDetailsPage /></LazyPage>
 </PermissionGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/shifts"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['global', 'branch']}>
 <PermissionGuard permission={['open_shift', 'close_shift', 'manage_shifts', 'access_admin_routes', 'process_sales']}>
 <LazyPage><ShiftsManagement /></LazyPage>
 </PermissionGuard>
 </ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/dining-halls"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission="manage_dining"><LazyPage><DiningHallsPage /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/waiter"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission={['process_sales', 'manage_dining']}><LazyPage><WaiterPOSPage /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/kitchen"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission={['view_kitchen', 'manage_kitchen']}><LazyPage><KitchenDisplayPage /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/kitchen/ticket/:id"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission={['view_kitchen', 'manage_kitchen']}><LazyPage><KitchenTicketPrintPage /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/kitchen/print-jobs"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission={['view_kitchen', 'manage_kitchen']}><LazyPage><KitchenPrintJobsPage /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 <Route
 path="/kitchen/stations"element={
 <AuthProtected>
 <NewLayout>
 <ViewModeGuard allowedModes={['branch']}><PermissionGuard permission="manage_kitchen"><LazyPage><KitchenStationsPage /></LazyPage></PermissionGuard></ViewModeGuard>
 </NewLayout>
 </AuthProtected>
 }
 />
 {authProtectedRoutes.map((route, idx) => (
 <Route
 path={route.path}
 element={
 <AuthProtected>
 <NewLayout><LazyPage>{route.component}</LazyPage></NewLayout>
 </AuthProtected>
 }
 key={idx}
 />
 ))}
 </Routes>
 </ErrorBoundary>
 )
}

export default Index
